<script setup lang="ts">
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";

const sections = [
  { title: "Practice Mode Short Questions", to: "/11th/computer/practice/short-questions/", description: "Mixed short questions ko hide/show answer mode ke sath revise karo." },
  { title: "SDLC Short Questions", to: "/11th/computer/sdlc/short-questions/", description: "Importance, phases, requirements aur UML ke short answers." },
  { title: "Python Short Questions", to: "/11th/computer/python/short-questions/", description: "Variables, loops aur operators ke short-answer prompts." },
  { title: "Algorithms Short Questions", to: "/11th/computer/algorithm/short-questions/", description: "Problem solving, searching aur sorting ke short answers." },
  { title: "Data Structures Short Questions", to: "/11th/computer/data-structures/short-questions/", description: "LIFO, FIFO, insert, stack aur queue differences." },
  { title: "Data Analytics Short Questions", to: "/11th/computer/data-analytics/short-questions/", description: "Models, visualization aur experiments ke short answers." },
  { title: "Emerging Technologies Short Questions", to: "/11th/computer/emerging-tech/short-questions/", description: "Cloud, blockchain aur serverless ke short-answer prompts." },
  { title: "Legal & Ethical Aspects Short Questions", to: "/11th/computer/legal-ethics/short-questions/", description: "Terms of use, digital divide aur safe internet behavior ke short answers." },
  { title: "Digital Literacy Short Questions", to: "/11th/computer/digital-literacy/short-questions/", description: "Reliable sources, keyword search aur plagiarism ke short answers." },
  { title: "Entrepreneurship Short Questions", to: "/11th/computer/entrepreneurship/short-questions/", description: "Entrepreneurship, design thinking aur marketing ke short answers." },
];

const supportLinks = [
  { title: "Pairing Scheme", to: "/11th/computer/pairing-scheme/", description: "Q2, Q3, Q4 blocks ko samajhne ke liye." },
  { title: "Important Questions", to: "/11th/computer/important-questions/", description: "Shortlist wale most important sawalat ke liye." },
  { title: "Practice Mode", to: "/11th/computer/practice/short-questions/", description: "Hide/show answers ke sath quick revision ke liye." },
];

const faqs = [
  {
    question: "11th class computer short questions kis tarah prepare karein?",
    answer:
      "Short questions ke liye pehle pairing scheme dekhein, phir chapter-wise short questions se high-yield topics revise karein. Python, SDLC aur Algorithms se start karna best route hota hai.",
  },
  {
    question: "1st year computer short questions kis chapters se zyada aate hain?",
    answer:
      "Chapter 1 SDLC, Chapter 2 Python, Chapter 3 Algorithms aur Chapter 4 Data Structures short questions ke liye zyada important chapters hain.",
  },
  {
    question: "Short questions hub aur practice mode me kya difference hai?",
    answer:
      "Short questions hub chapter-wise prep ke liye hai, jab ke practice mode mixed revision aur self-check ke liye useful hota hai.",
  },
];

usePageSeo({
  title: "11th Class Computer Short Questions | Ustaad",
  description: "11th class computer short questions ke liye chapter-wise hub: Python, SDLC, Algorithms aur baqi chapters ke short answers ek jagah.",
  keywords: "11th class computer short questions, first year computer short questions, chapter wise computer short questions",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Short Questions", item: "/11th/computer/short-questions/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer Short Questions Hub",
    items: sections.map((section) => ({ name: section.title, path: section.to })),
  }),
});
</script>

<template>
  <PageContainer>
    <section class="rounded-[30px] border border-slate-200 bg-gradient-to-br from-sky-50 via-white to-amber-50 px-5 py-6 shadow-sm dark:border-slate-800 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 md:px-8 md:py-8">
      <p class="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">Short Questions Hub</p>
      <h1 class="mt-3 text-3xl font-bold tracking-tight text-slate-950 dark:text-white md:text-5xl">11th Class Computer Short Questions</h1>
      <p class="mt-4 max-w-3xl text-sm leading-7 text-slate-700 dark:text-slate-300">
        Yahan se chapter-wise short questions kholo. Agar aapko direct short-answer revision chahiye ho, yeh page fastest route hai.
      </p>
      <p class="mt-3 max-w-3xl text-sm leading-7 text-slate-600 dark:text-slate-300">
        11th class computer paper me short questions selective blocks me aate hain, is liye chapter-wise short prep aur pairing dono sath dekhna zyada useful hota hai.
      </p>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Start Here</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Choose your chapter</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <NuxtLink
          v-for="section in sections"
          :key="section.to"
          :to="section.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ section.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ section.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">Related Prep</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Use short questions with pattern and pairing</h2>
      </div>
      <div class="mt-5 grid gap-4 md:grid-cols-3">
        <NuxtLink
          v-for="item in supportLinks"
          :key="item.to"
          :to="item.to"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-sky-300 hover:bg-white dark:border-slate-800 dark:bg-slate-950 dark:hover:border-sky-700 dark:hover:bg-slate-900"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ item.title }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ item.description }}</p>
        </NuxtLink>
      </div>
    </section>

    <section class="rounded-[28px] border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900 md:p-6">
      <div>
        <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
        <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">11th Computer Short Questions FAQs</h2>
      </div>
      <div class="mt-5 space-y-4">
        <article
          v-for="faq in faqs"
          :key="faq.question"
          class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
        >
          <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
          <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
        </article>
      </div>
    </section>
  </PageContainer>
</template>
